/* ==========================================================================
   BELORA — SKIMS PURE · bag motion
   Measured on live skims.com 2026-09-10 — g8-cart-ref.json

   Prestige and SKIMS agree on the DURATION (300ms) and disagree on the CURVE.

   Prestige animates the drawer with cubic-bezier(.645,.045,.355,1) — an
   ease-IN-OUT: slow, fast, slow. SKIMS's own CSS says
   `transition-transform duration-300 ease-out`, and the captured intermediate
   frames confirm it decelerates from the first frame:

     close @1440   990 -> 1138 -> 1267 -> 1368 -> 1410 -> 1433 -> gone
     step                148    129    100     43     23
     open  @1440  1440 -> 1292 -> 1163 -> 1082 -> 1020 ->  997 -> 990
     step                 148    129     81     62     23      7

   Monotonically shrinking steps = ease-out. An ease-in-out would start small,
   grow, then shrink. This is only visible in intermediate frames; a show()
   count or a start/end comparison cannot tell the two curves apart.

   So: keep Prestige's engine and override ONLY the two animation factories.
   The caller needs `.finished` (a promise) and `.finish()`, both of which the
   Web Animations API provides natively.
   ========================================================================== */

(function () {
  'use strict';

  /* Patch the Drawer BASE class, not cart-drawer: cart-drawer extends it, and
     so does the BELORA FIT panel. Two separate motion implementations would
     drift apart, and only one of them would carry the interruption fixes
     below. `x-drawer` IS the Drawer base in Prestige. */
  var CartDrawer = window.customElements.get('x-drawer');
  if (!CartDrawer) return;

  var DURATION = 300;                                  // measured
  var EASING = 'cubic-bezier(0, 0, 0.2, 1)';           // measured ease-out

  // A Motion-One-shaped handle over plain WAAPI animations.
  //
  // A superseded call's animations get .cancel()'d by interrupt() below,
  // which makes their native .finished REJECT. Left alone, Promise.all would
  // then reject too, and — worse — a caller who did `.finished.then(cb)`
  // would still see that .then() callback run for the SUPERSEDED call (a
  // rejected promise in Promise.all still lets .catch consumers observe it,
  // but here the real danger is theme.js's own
  // `hideTransitionPromise.then(() => display:none)` firing for a hide()
  // that a later show() has already superseded, forcing the drawer invisible
  // again right after it was told to reopen — confirmed live, see GATE 8
  // REOPEN motion re-verification). So a cancelled/superseded animation's
  // contribution is turned into a promise that never settles: the caller's
  // completion callback simply never fires for the stale call, and only the
  // LATEST call's .finished can ever resolve.
  function controls(anims) {
    var settled = anims.map(function (a) {
      return a.finished.catch(function () { return new Promise(function () {}); });
    });
    return {
      finished: Promise.all(settled).then(function () {}),
      finish: function () { anims.forEach(function (a) { try { a.finish(); } catch (e) {} }); },
      cancel: function () { anims.forEach(function (a) { try { a.cancel(); } catch (e) {} }); },
      stop: function () { anims.forEach(function (a) { try { a.cancel(); } catch (e) {} }); },
      play: function () { anims.forEach(function (a) { a.play(); }); },
      pause: function () { anims.forEach(function (a) { a.pause(); }); },
      get currentTime() { return anims[0] ? anims[0].currentTime : 0; },
    };
  }

  function parts(el) {
    if (typeof el.getShadowPartByName !== 'function') return null;
    var overlay = el.getShadowPartByName('overlay');
    var content = el.getShadowPartByName('content');
    return overlay && content ? { overlay: overlay, content: content } : null;
  }

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function opts() {
    return { duration: reduced.matches ? 0 : DURATION, easing: EASING, fill: 'both' };
  }

  // Rapid open/close/open (fire open, close before it finishes, open again
  // before THAT finishes) used to make the panel SNAP: each factory always
  // animated from a hardcoded keyframe (translateX(100%) or translateX(0)),
  // so retriggering mid-flight didn't continue from wherever the panel
  // currently was — it jumped there instantly, then animated from the jump.
  // Measured live: a ~140px instant jump the frame a second trigger fired
  // mid-animation (GATE 8 REOPEN motion re-verification, sp-g8m-belora
  // rapid trace). Fix: if the previous call's animations are still running,
  // read the CURRENTLY COMPOSITED transform/opacity before cancelling them,
  // and start the new animation from there — the same "retarget from
  // current value" behaviour CSS transitions give you for free. When
  // nothing was running (the normal, uninterrupted case) this is a no-op
  // and the original hardcoded keyframes are used, unchanged.
  function interrupt(el) {
    var prev = el.__skAnims;
    var wasRunning = !!(prev && prev.some(function (a) { return a.playState === 'running'; }));
    if (prev) prev.forEach(function (a) { try { a.cancel(); } catch (e) {} });
    return wasRunning;
  }

  CartDrawer.prototype.createEnterAnimationControls = function () {
    var p = parts(this);
    if (!p) return { finished: Promise.resolve(), finish: function () {} };
    var liveOpacity = getComputedStyle(p.overlay).opacity;
    var liveTransform = getComputedStyle(p.content).transform;
    var wasRunning = interrupt(this);
    // Prestige sets this so a right-hand drawer hugs the right edge; the panel
    // is mis-placed without it.
    p.content.style.marginInlineStart = 'auto';
    var anims = [
      p.overlay.animate([{ opacity: wasRunning ? liveOpacity : 0 }, { opacity: 1 }], opts()),
      p.content.animate([{ transform: wasRunning ? liveTransform : 'translateX(100%)' }, { transform: 'translateX(0)' }], opts()),
    ];
    this.__skAnims = anims;
    return controls(anims);
  };

  CartDrawer.prototype.createLeaveAnimationControls = function () {
    var p = parts(this);
    if (!p) return { finished: Promise.resolve(), finish: function () {} };
    var liveOpacity = getComputedStyle(p.overlay).opacity;
    var liveTransform = getComputedStyle(p.content).transform;
    var wasRunning = interrupt(this);
    var anims = [
      p.overlay.animate([{ opacity: wasRunning ? liveOpacity : 1 }, { opacity: 0 }], opts()),
      p.content.animate([{ transform: wasRunning ? liveTransform : 'translateX(0)' }, { transform: 'translateX(100%)' }], opts()),
    ];
    this.__skAnims = anims;
    return controls(anims);
  };

  /* Prestige's dialog gates its own Escape handling behind
     createEnterAnimationControls().finished — the focus trap is activated
     with `checkCanFocusTrap: () => animationControls.finished`, and its
     Escape listener only exists once the trap is active. Prestige's stock
     animation resolves .finished in ~0ms (duration 0), so that gate is
     invisible there. This override's .finished is honest (Promise.all of
     the real 300ms WAAPI animations), which opens a real ~300ms window,
     measured live, where Escape does nothing while the drawer is still
     opening (confirmed: no effect at 50/100/200ms into the enter animation,
     works again exactly once its currentTime reaches 300/300 — see GATE 8
     REOPEN motion re-verification, sp-g8m-diag-escape.mjs).

     Fix: close on Escape independently of the focus trap. hide() no-ops
     when already closed, so this safely coexists with Prestige's own
     (delayed) handler once it does activate. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('cart-drawer.sk-cart[open], x-drawer.sk-size[open]');
    if (open && typeof open.hide === 'function') open.hide();
  });
})();


/* ==========================================================================
   BELORA ACCESS · sepet içi
   Renk seçimi gerçek varyantı forma yazar, Prestige'in <product-form>'u
   ekler. İkinci bir sepet state'i YOK; "Eklendi" durumu sunucudan gelen
   gerçek sepet içeriğinden okunur (bkz. sk-access.liquid), burada
   uydurulmaz.
   ========================================================================== */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var chip = event.target.closest('[data-sk-access-variant]');
    if (chip) {
      var root = chip.closest('[data-sk-access]');
      if (!root) return;
      if (chip.getAttribute('aria-disabled') === 'true') {
        var err = root.querySelector('[data-sk-access-error]');
        if (err) { err.textContent = 'Bu renk şu anda mevcut değil.'; err.hidden = false; }
        return;
      }
      Array.prototype.forEach.call(root.querySelectorAll('[data-sk-access-variant]'), function (c) {
        c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
      });
      var idInput = root.querySelector('[data-sk-access-id]');
      var addBtn = root.querySelector('[data-sk-access-add]');
      var errBox = root.querySelector('[data-sk-access-error]');
      if (idInput) { idInput.value = chip.getAttribute('data-sk-access-variant'); idInput.disabled = false; }
      if (addBtn) addBtn.disabled = false;
      if (errBox) errBox.hidden = true;
      return;
    }

    var add = event.target.closest('[data-sk-access-add]');
    if (!add) return;
    var scope = add.closest('[data-sk-access]');
    if (!scope) return;
    var single = scope.querySelector('[data-sk-access-single]');
    var field = scope.querySelector('[data-sk-access-id]');
    // Single-variant product: no colour to choose, so fill the id on submit.
    if (single && field && !field.value) { field.value = single.value; field.disabled = false; }
    if (field && !field.value) {
      event.preventDefault();
      var e2 = scope.querySelector('[data-sk-access-error]');
      if (e2) { e2.textContent = 'Lütfen bir renk seçin.'; e2.hidden = false; }
    }
  });

  /* Gift row: ticking the box submits the gift's own product form (Prestige
     adds it and re-renders the bag, where the row no longer appears). */
  document.addEventListener('change', function (event) {
    var cb = event.target.closest && event.target.closest('[data-sk-gift-cb]');
    if (!cb || !cb.checked) return;
    var form = cb.form;
    if (!form) return;
    cb.disabled = true;
    if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  });

  document.addEventListener('cart:error', function (event) {
    var box = document.querySelector('[data-sk-access-error]');
    var msg = event.detail && (event.detail.error || event.detail.description || event.detail.message);
    if (box && msg) { box.textContent = msg; box.hidden = false; }
  });
})();

/* ==========================================================================
   Line actions: Edit, Move to Wishlist, and the /cart page re-render.

   Measured on the reference's bag with a real line in it:
     Edit              opens an "Edit Item" view over the bag: product, the
                       option's value grid, CONFIRM CHANGES, Back to Cart
     Move to Wishlist  the line leaves the bag; the item is in the wishlist

   Neither adds a cart write path of its own:
     - Move clicks the line's full-remove /cart/change link, which lives inside
       <line-item-quantity>: Prestige's engine does the AJAX change, bundles the
       sections and re-renders the drawer. It only does so once the wishlist
       has verifiably taken the item; if storage is blocked the line stays.
     - Edit adds the new variant first and removes the old line only after
       Shopify accepts the add, then asks Prestige to re-render through
       cart:refresh. A failure can leave both lines, never neither.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  /* ---------------------------------------------------------------- move */
  function onMove(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;          // double click
    var W = window.BeloraWishlist;
    if (!W || !W.add) return;
    var item = {
      handle: btn.getAttribute('data-wl-handle'),
      title: btn.getAttribute('data-wl-title') || '',
      url: btn.getAttribute('data-wl-url') || '',
      image: btn.getAttribute('data-wl-image') || '',
      price: btn.getAttribute('data-wl-price') || ''
    };
    btn.setAttribute('aria-busy', 'true');
    W.add(item);
    if (!W.has(item.handle)) {                                      // storage blocked
      btn.removeAttribute('aria-busy');
      return;
    }
    var line = btn.closest('line-item');
    var rm = line && line.querySelector('[data-sk-remove]');
    if (!rm) { btn.removeAttribute('aria-busy'); return; }
    rm.click();                                                     // Prestige engine
    // On success the drawer re-renders and this button is gone. If the change
    // failed, Prestige has shown its own error; hand the control back.
    window.setTimeout(function () { if (btn.isConnected) btn.removeAttribute('aria-busy'); }, 6000);
  }

  /* ---------------------------------------------------------------- edit */
  // One group per option axis (colour, then size, as on the reference). A
  // value is unavailable when no available variant exists with it AND the
  // other axes as currently chosen.
  var edit = { data: null, scope: null, chosen: null, busy: false, opener: null };

  function panelFor(el) {
    var scope = el.closest('cart-drawer, [data-sk-cartpage]');
    return scope ? scope.querySelector('[data-sk-edit]') : null;
  }

  function variantFor(opts) {
    return edit.data.variants.filter(function (v) {
      return v.o.every(function (x, i) { return x === opts[i]; });
    })[0] || null;
  }

  /* The reference's /cart Edit locks the page behind it: html { overflow:
     hidden } while the panel is open (measured 12 Sep 2026). Ours let the page
     scroll under the sheet, so a long /cart moved behind a pinned panel.
     Only on the cart PAGE - inside the bag drawer Prestige already holds the
     page, and a second lock would fight its own restore. */
  function pageLock(on) {
    if (!document.querySelector('[data-sk-cartpage]')) return;
    var el = document.documentElement;
    if (on) {
      if (!el.hasAttribute('data-sk-edit-lock')) {
        el.setAttribute('data-sk-edit-lock', el.style.overflow || '');
        el.style.overflow = 'hidden';
      }
    } else if (el.hasAttribute('data-sk-edit-lock')) {
      el.style.overflow = el.getAttribute('data-sk-edit-lock');
      el.removeAttribute('data-sk-edit-lock');
    }
  }

  function openEdit(btn) {
    var line = btn.closest('line-item');
    var json = line && line.querySelector('[data-sk-edit-data]');
    var panel = panelFor(btn);
    if (!json || !panel) return;
    var data;
    try { data = JSON.parse(json.textContent); } catch (e) { return; }
    edit.data = data; edit.scope = panel; edit.chosen = data.current.slice(); edit.opener = btn;

    panel.querySelector('[data-sk-edit-family]').textContent = data.family || '';
    panel.querySelector('[data-sk-edit-name]').textContent = data.title || '';
    panel.querySelector('[data-sk-edit-price]').textContent = data.price || '';
    // The markdown, as the bag line and the reference's Edit view both show it.
    var wasEl = panel.querySelector('[data-sk-edit-was]');
    var prices = panel.querySelector('.sk-edit__prices');
    if (wasEl) {
      wasEl.textContent = data.was || '';
      wasEl.hidden = !data.was;
    }
    if (prices) prices.classList.toggle('is-marked', !!data.was);
    var link = panel.querySelector('[data-sk-edit-link]');
    if (link) { if (data.url) link.setAttribute('href', data.url); else link.removeAttribute('href'); }

    var groups = panel.querySelector('[data-sk-edit-groups]');
    groups.textContent = '';
    data.options.forEach(function (opt, gi) {
      var head = document.createElement('p');
      head.className = 'sk-edit__label';
      var name = document.createElement('span'); name.textContent = opt.name;
      var val = document.createElement('b'); val.setAttribute('data-sk-edit-optval', gi);
      head.appendChild(name); head.appendChild(document.createTextNode(' ')); head.appendChild(val);
      // Dots for a colour axis whose every value has a paint (Shopify swatch
      // or the theme's colour map — sk-swatch.liquid); if any value has none,
      // the whole axis is written out as names, exactly as the PDP decides.
      var dots = opt.colour && opt.values.length > 0 && opt.values.every(function (vv) { return !!vv.swatch; });
      var list = document.createElement('ul');
      list.className = 'sk-edit__values unstyled-list' + (dots ? ' sk-edit__values--colour' : '');
      list.setAttribute('role', 'list');
      opt.values.forEach(function (vv) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('data-sk-edit-value', vv.v);
        b.setAttribute('data-sk-edit-group', gi);
        if (dots) {
          b.className = 'sk-edit__dot';
          b.style.setProperty('--sk-dot', vv.swatch);
          b.style.setProperty('--sk-ring', vv.ring || vv.swatch);   // sk-swatch-ring.liquid
          b.setAttribute('aria-label', vv.v);
        } else {
          b.className = 'sk-edit__value';
          b.textContent = vv.v;
        }
        li.appendChild(b);
        list.appendChild(li);
      });
      groups.appendChild(head);
      groups.appendChild(list);
    });
    paintChoice();
    var err = panel.querySelector('[data-sk-edit-error]');
    if (err) err.hidden = true;
    // In: unhide in the closed pose, commit it, then let the sheet slide
    // (sk-cart.css — 300ms cubic-bezier(0,0,.2,1), from the right at 768 and
    // up, from the bottom over a scrim below).
    window.clearTimeout(panel.__skEditT);
    panel.hidden = false;
    void panel.offsetWidth;
    panel.classList.add('is-open');
    pageLock(true);
    var first = panel.querySelector('[aria-pressed="true"]') || panel.querySelector('[data-sk-edit-close]');
    if (first) first.focus({ preventScroll: true });
  }

  function paintChoice() {
    if (!edit.scope || !edit.data) return;
    var d = edit.data;
    edit.scope.querySelectorAll('[data-sk-edit-optval]').forEach(function (b) {
      b.textContent = edit.chosen[Number(b.getAttribute('data-sk-edit-optval'))] || '';
    });
    edit.scope.querySelectorAll('[data-sk-edit-value]').forEach(function (b) {
      var gi = Number(b.getAttribute('data-sk-edit-group'));
      var v = b.getAttribute('data-sk-edit-value');
      b.setAttribute('aria-pressed', edit.chosen[gi] === v ? 'true' : 'false');
      var probe = edit.chosen.slice(); probe[gi] = v;
      var hit = variantFor(probe);
      var ok = !!(hit && hit.available);
      b.classList.toggle('is-unavailable', !ok);
      if (ok) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
    });
    var target = variantFor(edit.chosen);
    var img = edit.scope.querySelector('[data-sk-edit-img]');
    var src = (target && target.image) || d.image;
    if (img) { if (src) { img.src = src; img.hidden = false; } else { img.hidden = true; } }
    var confirm = edit.scope.querySelector('[data-sk-edit-confirm]');
    if (confirm) confirm.disabled = edit.busy || !target || !target.available || target.id === d.variant;
  }

  function closeEdit() {
    if (!edit.scope) return;
    var p = edit.scope;
    // Out the way it came, the same 300ms; hidden once it has left.
    p.classList.remove('is-open');
    pageLock(false);
    var gone = function () { if (!p.classList.contains('is-open')) p.hidden = true; };
    window.clearTimeout(p.__skEditT);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) gone();
    else p.__skEditT = window.setTimeout(gone, 320);
    if (edit.opener && edit.opener.isConnected) edit.opener.focus({ preventScroll: true });
    edit.scope = null; edit.data = null; edit.opener = null;
  }

  function confirmEdit(btn) {
    if (edit.busy || !edit.data) return;
    var target = variantFor(edit.chosen);
    if (!target || !target.available || target.id === edit.data.variant) return;
    edit.busy = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    var scope = edit.scope, data = edit.data;
    var err = scope.querySelector('[data-sk-edit-error]');
    var onPage = !!scope.closest('[data-sk-cartpage]');
    var done = function () { edit.busy = false; btn.removeAttribute('aria-busy'); };

    fetch(ROOT + 'cart/add.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: target.id, quantity: data.quantity }] })
    }).then(function (added) {
      if (added.ok) return added.json();
      return added.json().catch(function () { return {}; }).then(function (j) {
        throw new Error(j.description || j.message || 'Bu seçenek şu anda eklenemiyor.');
      });
    }).then(function () {
      // The new line is in. Take the old one out; even if that fails, the
      // re-render below shows exactly what the bag now holds.
      return fetch(ROOT + 'cart/change.js', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ id: data.key, quantity: 0 })
      }).catch(function () {});
    }).then(function () {
      if (onPage) { window.location.reload(); return; }
      scope.classList.remove('is-open');
      scope.hidden = true;
      edit.scope = null; edit.data = null;
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      done();
    }).catch(function (e) {
      if (err) { err.textContent = e.message; err.hidden = false; }
      btn.disabled = false;
      done();
    });
  }

  /* ------------------------------------------------------------- routing */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;
    var mv = t.closest('[data-sk-move]');
    if (mv) { e.preventDefault(); onMove(mv); return; }
    var op = t.closest('[data-sk-edit-open]');
    if (op) { e.preventDefault(); openEdit(op); return; }
    var cl = t.closest('[data-sk-edit-close]');
    if (cl) { e.preventDefault(); closeEdit(); return; }
    var val = t.closest('[data-sk-edit-value]');
    if (val) {
      e.preventDefault();
      if (edit.busy || !edit.chosen) return;
      var gi = Number(val.getAttribute('data-sk-edit-group'));
      var v = val.getAttribute('data-sk-edit-value');
      // Choosing a colour that has no variant in the current size still
      // selects it; the size grid then shows what that colour offers.
      if (val.getAttribute('aria-disabled') === 'true' && gi === edit.data.options.length - 1) return;
      edit.chosen[gi] = v;
      paintChoice();
      return;
    }
    var cf = t.closest('[data-sk-edit-confirm]');
    if (cf) { e.preventDefault(); confirmEdit(cf); }
  });

  // Escape closes the edit view first, not the whole drawer under it.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && edit.scope && edit.scope.classList.contains('is-open')) { e.stopPropagation(); closeEdit(); }
  }, true);

  /* ---------------------------------------------------- the /cart page
     Its stepper already wrote to the cart through Prestige's engine, but the
     page never listened for the result, so it kept showing the old quantity.
     Register it for Prestige's bundled sections and swap its HTML in when the
     change lands: the same contract Prestige's own drawer uses. */
  /* Sections bound to the cart's state (the template's "Explore More" rail,
     shown only while the bag is empty) ride along and are swapped whole. */
  function boundIds() {
    return Array.prototype.map.call(document.querySelectorAll('[data-sk-cart-refresh]'), function (m) {
      return m.getAttribute('data-sk-cart-refresh');
    });
  }
  document.addEventListener('cart:prepare-bundled-sections', function (e) {
    var page = document.querySelector('[data-sk-cartpage]');
    if (!page || !e.detail || !e.detail.sections) return;
    e.detail.sections.push(page.getAttribute('data-section-id'));
    boundIds().forEach(function (id) { if (e.detail.sections.indexOf(id) < 0) e.detail.sections.push(id); });
  });
  document.addEventListener('cart:change', function (e) {
    var page = document.querySelector('[data-sk-cartpage]');
    if (!page) return;
    var sections = e.detail && e.detail.cart && e.detail.cart.sections;
    if (!sections) return;
    var id = page.getAttribute('data-section-id');
    var html = sections[id];
    if (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-sk-cartpage]');
      if (fresh) page.replaceWith(fresh);
    }
    boundIds().forEach(function (bid) {
      var markup = sections[bid];
      var live = document.getElementById('shopify-section-' + bid);
      if (!markup || !live) return;
      var d = new DOMParser().parseFromString(markup, 'text/html');
      var next = d.getElementById('shopify-section-' + bid);
      if (next) live.replaceWith(next);
    });
    document.dispatchEvent(new CustomEvent('sk:rails:refresh'));
    initCartRails();
  });

  /* ------------------------------------------- the /cart page's own rails
     Arrows read disabled when a rail has nothing to page (sk-rail.js pages
     them). The second rail is Shopify's "related" for the first line: when
     it arrives, anything the rail above already shows is taken out, and a
     rail left with nothing is not drawn at all. */
  function paintCartRail(rail) {
    var t = rail.querySelector('[data-sk-rail-track]');
    if (!t) return;
    var can = t.scrollWidth - t.clientWidth > 1;
    Array.prototype.forEach.call(rail.querySelectorAll('[data-sk-rail-prev], [data-sk-rail-next]'), function (b) { b.disabled = !can; });
  }
  function settleRecs(rail) {
    var shown = {};
    Array.prototype.forEach.call(document.querySelectorAll('[data-sk-rail="cart"]:not([data-sk-cart-recs]) .sk-crc[data-wl-handle]'), function (c) {
      shown[c.getAttribute('data-wl-handle')] = true;
    });
    var cards = rail.querySelectorAll('.sk-crc[data-wl-handle]');
    Array.prototype.forEach.call(cards, function (c) { if (shown[c.getAttribute('data-wl-handle')]) c.remove(); });
    var left = rail.querySelectorAll('.sk-crc[data-wl-handle]').length;
    rail.hidden = left === 0;
    if (left) paintCartRail(rail);
  }
  function initCartRails() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sk-rail="cart"]'), function (rail) {
      if (rail.hasAttribute('data-sk-cart-recs')) {
        if (rail.__skObs) return;
        rail.__skObs = new MutationObserver(function () { settleRecs(rail); });
        rail.__skObs.observe(rail, { childList: true, subtree: true });
        if (rail.querySelector('.sk-crc')) settleRecs(rail);
      } else {
        paintCartRail(rail);
      }
    });
  }
  window.addEventListener('resize', function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sk-rail="cart"]:not([hidden])'), paintCartRail);
  }, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCartRails);
  else initCartRails();

  /* ------------------------------------- summary rows (terms / shipping / returns)
     The reference's disclosure: 200ms ease-in-out on height, the chevron
     turning with it. Reduced motion opens and closes at once. */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-sk-acc]');
    if (!btn) return;
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    var open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel.__skAnim) { panel.__skAnim.cancel(); panel.__skAnim = null; }
    if (open) panel.hidden = false;
    if (reduceMotion.matches || !panel.animate) { panel.hidden = !open; return; }
    var h = panel.scrollHeight;
    var a = panel.animate(open ? [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }]
                               : [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
                          { duration: 200, easing: 'cubic-bezier(.4, 0, .2, 1)' });
    panel.__skAnim = a;
    a.onfinish = function () { panel.__skAnim = null; if (!open) panel.hidden = true; };
  });
})();

/* ==========================================================================
   Discount code row.

   Applied for real and read back, never assumed:
     1. the codes already on the cart are read from /cart.js and kept, so a
        mistyped second code cannot knock out a valid first one;
     2. the Ajax cart is asked to apply the list. If Shopify answers with a
        discount_codes array, that answer is the truth: only a code it marks
        applicable is reported as applied;
     3. only if the reply shows the field was ignored, fall back to Shopify's
        own /discount/{code} route. That route strips the redirect's query
        string, so the code rides in sessionStorage and is verified against
        /cart.js on the way back.
   Measured on this store 11 Sep 2026: /cart/update.js DOES take `discount`
   and answers with discount_codes [{code, applicable}] — the in-place path is
   the live one. A rejected code is written back out (the list is restored),
   so nothing inapplicable rides along to checkout.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var KEY = 'belora:code-try';
  var HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  function up(s) { return String(s || '').trim().toUpperCase(); }

  function appliedCodes(cart) {
    var out = [];
    (cart.discount_codes || []).forEach(function (d) { if (d && d.applicable) out.push(up(d.code)); });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      if (d && d.type === 'discount_code') out.push(up(d.title));
    });
    (cart.items || []).forEach(function (it) {
      (it.line_level_discount_allocations || []).forEach(function (a) {
        var app = a && a.discount_application;
        if (app && app.type === 'discount_code') out.push(up(app.title));
      });
    });
    return out.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
  }

  function scopeOf(el) { return el && el.closest ? el.closest('[data-sk-code]') : null; }

  function say(scope, text, kind) {
    var m = scope && scope.querySelector('[data-sk-code-msg]');
    if (!m) return;
    m.textContent = text || '';
    m.hidden = !text;
    m.setAttribute('data-kind', kind || '');
  }

  // The panel collapses rather than cutting: 200ms on a max-height cap and opacity, both ways, to
  // match the reference (see the note over .sk-code__panel in sk-cart.css). `hidden` still carries
  // the state for assistive tech and for a fresh render — it is only set once the close has run,
  // the way the reference hides its own panel at the end of the transition.
  var PANEL_MS = 200;
  function openPanel(scope, open) {
    var t = scope.querySelector('[data-sk-code-toggle]');
    var p = scope.querySelector('[data-sk-code-panel]');
    if (!t || !p) return;
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (p._skHide) { clearTimeout(p._skHide); p._skHide = 0; }
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (open) {
      p.hidden = false;
      void p.offsetWidth;                 // let the closed state paint before the class flips
      p.classList.add('is-open');
      return;
    }
    p.classList.remove('is-open');
    if (still) { p.hidden = true; return; }
    p._skHide = setTimeout(function () { p._skHide = 0; p.hidden = true; }, PANEL_MS);
  }

  // After a successful apply the drawer re-renders from the server, which
  // replaces this row. Carry the message across the re-render.
  function sayAfterRefresh(text, kind) {
    document.addEventListener('cart-drawer:refreshed', function once() {
      document.removeEventListener('cart-drawer:refreshed', once);
      var sc = document.querySelector('cart-drawer [data-sk-code]');
      if (sc) { openPanel(sc, true); say(sc, text, kind); }
    });
  }

  // On the /cart page the row belongs to the page, not the drawer: re-render
  // the page section so the total shows the code, then answer in its row.
  function refreshPage(text, kind) {
    var page = document.querySelector('[data-sk-cartpage]');
    if (!page) return;
    fetch(ROOT + 'cart?section_id=' + encodeURIComponent(page.getAttribute('data-section-id')))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sk-cartpage]');
        if (fresh) page.replaceWith(fresh);
        var sc = document.querySelector('[data-sk-cartpage] [data-sk-code]');
        if (sc) { openPanel(sc, true); say(sc, text, kind); }
      })
      .catch(function () { window.location.reload(); });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-sk-code-toggle]');
    if (!t) return;
    var sc = scopeOf(t);
    var open = t.getAttribute('aria-expanded') !== 'true';
    openPanel(sc, open);
    if (open) { var f = sc.querySelector('[data-sk-code-field]'); if (f) f.focus({ preventScroll: true }); }
  });

  // The Apply button reads as disabled (grey) until there is something to apply.
  document.addEventListener('input', function (e) {
    var f = e.target.closest && e.target.closest('[data-sk-code-field]');
    if (!f) return;
    var sc = scopeOf(f);
    var b = sc && sc.querySelector('[data-sk-code-apply]');
    if (b) b.classList.toggle('is-ready', f.value.trim().length > 0);
    say(sc, '', '');
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-sk-code-form]');
    if (!form) return;
    e.preventDefault();
    var sc = scopeOf(form);
    var field = sc.querySelector('[data-sk-code-field]');
    var btn = sc.querySelector('[data-sk-code-apply]');
    var code = (field.value || '').trim();
    if (!code) { say(sc, 'Lütfen bir kod girin.', 'error'); field.focus(); return; }
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    say(sc, '', '');

    var release = function () { btn.removeAttribute('aria-busy'); btn.disabled = false; };

    var keep = [];
    fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        keep = appliedCodes(cart).filter(function (c) { return c !== up(code); });
        return fetch(ROOT + 'cart/update.js', {
          method: 'POST', headers: HEADERS,
          body: JSON.stringify({ discount: keep.concat(code).join(',') })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Did Shopify actually process the field? Then the code we sent is in
        // discount_codes, applicable or not. If it is absent, the field was
        // ignored: a "not valid" message here would be a guess, so hand over
        // to the /discount route, which is authoritative.
        var seen = (cart && cart.discount_codes || []).some(function (d) { return d && up(d.code) === up(code); });
        if (!seen) { throw { fallback: true }; }
        if (appliedCodes(cart).indexOf(up(code)) >= 0) {
          if (sc.closest('[data-sk-cartpage]')) refreshPage('"' + code + '" uygulandı.', 'ok');
          else sayAfterRefresh('"' + code + '" uygulandı.', 'ok');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } else {
          // Shopify keeps a rejected code on the cart as "not applicable" and
          // would carry it to checkout. Put the cart's code list back to what
          // it was before this attempt.
          fetch(ROOT + 'cart/update.js', {
            method: 'POST', headers: HEADERS,
            body: JSON.stringify({ discount: keep.join(',') })
          }).catch(function () {});
          say(sc, 'Bu kod geçerli değil ya da bu sepete uygulanamıyor.', 'error');
        }
        release();
      })
      .catch(function (err) {
        if (err && err.fallback) {
          try { window.sessionStorage.setItem(KEY, code); } catch (x) { /* storage blocked */ }
          window.location.href = ROOT + 'discount/' + encodeURIComponent(code)
            + '?redirect=' + encodeURIComponent(window.location.pathname);
          return;
        }
        say(sc, 'Kod şu anda uygulanamadı. Lütfen tekrar deneyin.', 'error');
        release();
      });
  });

  // Back from /discount/{code}: verify against the cart, then show the answer
  // where the customer asked the question.
  function verifyReturn() {
    var code = null;
    try { code = window.sessionStorage.getItem(KEY); window.sessionStorage.removeItem(KEY); } catch (x) { return; }
    if (!code) return;
    fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ok = appliedCodes(cart).indexOf(up(code)) >= 0;
        var onPage = document.querySelector('[data-sk-cartpage] [data-sk-code]');
        var drawer = onPage ? null : document.querySelector('cart-drawer');
        var sc = onPage || document.querySelector('cart-drawer [data-sk-code]') || document.querySelector('[data-sk-code]');
        if (sc) {
          openPanel(sc, true);
          say(sc, ok ? '"' + code + '" uygulandı.' : 'Bu kod geçerli değil ya da bu sepete uygulanamıyor.', ok ? 'ok' : 'error');
        }
        if (drawer && typeof drawer.show === 'function' && cart.item_count > 0) drawer.show();
      })
      .catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', verifyReturn);
  else verifyReturn();
})();

/* Bag rail cards: the size list and the add button that waits for it are
   assets/sk-listbox.js, shared with the product page's "Complete the Look". */

/* Empty bag rail. The reference shows "From Your Wishlist" when the wishlist
   holds something and "Explore More" when it does not, with the same cards
   either way. Every card is rendered server-side (so every add goes through
   Prestige's own <product-form>); here we only choose which ones to show and
   which heading to use, from the wishlist this browser actually holds. */
(function () {
  'use strict';
  function paintEmptyRail() {
    var rail = document.querySelector('cart-drawer [data-sk-empty-rail]');
    if (!rail) return;
    var W = window.BeloraWishlist;
    var saved = [];
    try { saved = (W && W.read ? W.read() : []).map(function (i) { return i.handle; }); } catch (e) { saved = []; }
    var cards = Array.prototype.slice.call(rail.querySelectorAll('.sk-crc[data-wl-handle]'));
    var hits = cards.filter(function (c) { return saved.indexOf(c.getAttribute('data-wl-handle')) >= 0; });
    var title = rail.querySelector('[data-sk-empty-rail-title]');
    if (hits.length) {
      cards.forEach(function (c) { c.hidden = hits.indexOf(c) < 0; });
      if (title) title.textContent = rail.getAttribute('data-title-wishlist');
    } else {
      cards.forEach(function (c) { c.hidden = false; });
      if (title) title.textContent = rail.getAttribute('data-title-explore');
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintEmptyRail);
  else paintEmptyRail();
  document.addEventListener('cart-drawer:refreshed', paintEmptyRail);
  document.addEventListener('cart:change', function () { window.setTimeout(paintEmptyRail, 0); });
  document.documentElement.addEventListener('wishlist:change', paintEmptyRail);
})();
